import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  fetchCaseList,
  CaseListItem,
  CaseListSort,
  CaseListSortDir,
  CaseListState,
  CASE_LIST_PAGE_SIZE,
} from '../../api/CaseListApi';
import { deriveStageLabel, isClosedStage } from '../../utils/caseStage';
import { caseCounty } from './caseCounty';

/**
 * Firm-wide open / closed case list (Lloyd, Aug 7: "opened cases and closed cases on the main menu,
 * and sorting accordingly").
 *
 * One component serves all three states — the route decides which. They are the same table with the
 * same sorting; splitting them into separate components would mean maintaining the same columns
 * three times.
 */

const STATE_TITLES: Record<CaseListState, string> = {
  open: 'Open Cases',
  closed: 'Closed Cases',
  all: 'All Cases',
};

const SORT_LABELS: Record<CaseListSort, string> = {
  courtDate: 'Court date',
  lastActivity: 'Last activity',
  clientName: 'Client name',
  created: 'Date opened',
};

const SORT_VALUES: CaseListSort[] = [
  'courtDate',
  'lastActivity',
  'clientName',
  'created',
];

/**
 * Default sort per list.
 *
 * Open cases lead with the soonest court date because that is the deadline staff work to. Closed
 * cases have no upcoming deadline, so they lead with the most recently closed.
 */
const DEFAULT_SORT: Record<CaseListState, { by: CaseListSort; dir: CaseListSortDir }> = {
  open: { by: 'courtDate', dir: 'asc' },
  closed: { by: 'lastActivity', dir: 'desc' },
  all: { by: 'courtDate', dir: 'asc' },
};

function parseState(raw: string | undefined): CaseListState {
  if (raw === 'closed' || raw === 'all') return raw;
  return 'open';
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US');
}

/**
 * "34 days ago" beside the date. The quiet-time reading is the whole reason last activity is on
 * screen — a raw timestamp makes staff do the subtraction themselves.
 */
function describeAge(value: string | null): string {
  if (!value) return '—';
  const then = new Date(value).getTime();
  if (isNaN(then)) return '—';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const ManagerCasesView: React.FC = () => {
  const navigate = useNavigate();
  const { state: stateParam } = useParams<{ state: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = parseState(stateParam);

  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<CaseListSort>(DEFAULT_SORT[state].by);
  const [sortDir, setSortDir] = useState<CaseListSortDir>(DEFAULT_SORT[state].dir);
  // Committed search term (what the server was asked for) vs the text in the box, so typing does
  // not fire a request per keystroke against a 5,000-case table.
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(
    async (opts: {
      nextState: CaseListState;
      nextSortBy: CaseListSort;
      nextSortDir: CaseListSortDir;
      nextSearch: string;
      from: number;
      append: boolean;
    }) => {
      if (opts.append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const result = await fetchCaseList({
          state: opts.nextState,
          sortBy: opts.nextSortBy,
          sortDir: opts.nextSortDir,
          search: opts.nextSearch,
          from: opts.from,
          size: CASE_LIST_PAGE_SIZE,
        });
        setCases((prev) =>
          opts.append ? [...prev, ...result.cases] : result.cases,
        );
        setTotal(result.total);
      } catch (err) {
        console.error('Error loading cases:', err);
        setError(err instanceof Error ? err.message : 'Failed to load cases');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  // Restore sort + search from the URL so a filtered view can be bookmarked or passed to someone
  // else, and re-run whenever the route switches between open and closed.
  useEffect(() => {
    const urlSortBy = searchParams.get('sortBy') as CaseListSort | null;
    const urlSortDir = searchParams.get('sortDir') as CaseListSortDir | null;
    const urlSearch = searchParams.get('search') ?? '';

    const nextSortBy =
      urlSortBy && SORT_VALUES.includes(urlSortBy)
        ? urlSortBy
        : DEFAULT_SORT[state].by;
    const nextSortDir =
      urlSortDir === 'asc' || urlSortDir === 'desc'
        ? urlSortDir
        : DEFAULT_SORT[state].dir;

    setSortBy(nextSortBy);
    setSortDir(nextSortDir);
    setSearch(urlSearch);
    setSearchInput(urlSearch);
    load({
      nextState: state,
      nextSortBy,
      nextSortDir,
      nextSearch: urlSearch,
      from: 0,
      append: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const syncUrl = (
    nextSortBy: CaseListSort,
    nextSortDir: CaseListSortDir,
    nextSearch: string,
  ) => {
    const params = new URLSearchParams();
    if (nextSortBy !== DEFAULT_SORT[state].by) params.set('sortBy', nextSortBy);
    if (nextSortDir !== DEFAULT_SORT[state].dir)
      params.set('sortDir', nextSortDir);
    if (nextSearch.trim()) params.set('search', nextSearch.trim());
    setSearchParams(params, { replace: true });
  };

  const applySort = (nextSortBy: CaseListSort, nextSortDir: CaseListSortDir) => {
    setSortBy(nextSortBy);
    setSortDir(nextSortDir);
    syncUrl(nextSortBy, nextSortDir, search);
    load({
      nextState: state,
      nextSortBy,
      nextSortDir,
      nextSearch: search,
      from: 0,
      append: false,
    });
  };

  const handleSearch = () => {
    setSearch(searchInput);
    syncUrl(sortBy, sortDir, searchInput);
    load({
      nextState: state,
      nextSortBy: sortBy,
      nextSortDir: sortDir,
      nextSearch: searchInput,
      from: 0,
      append: false,
    });
  };

  const handleClear = () => {
    setSearchInput('');
    setSearch('');
    syncUrl(sortBy, sortDir, '');
    load({
      nextState: state,
      nextSortBy: sortBy,
      nextSortDir: sortDir,
      nextSearch: '',
      from: 0,
      append: false,
    });
  };

  const handleLoadMore = () => {
    load({
      nextState: state,
      nextSortBy: sortBy,
      nextSortDir: sortDir,
      nextSearch: search,
      from: cases.length,
      append: true,
    });
  };

  const hasMore = cases.length < total;

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" component="h1">
            {STATE_TITLES[state]}
          </Typography>
          {!loading && (
            <Typography variant="body1" color="text.secondary">
              {total.toLocaleString('en-US')} case{total === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search client, matter # or citation"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
            />

            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e: SelectChangeEvent) =>
                  applySort(e.target.value as CaseListSort, sortDir)
                }
              >
                {SORT_VALUES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {SORT_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip
              title={
                sortDir === 'asc'
                  ? 'Oldest / soonest first — click for the reverse'
                  : 'Newest / latest first — click for the reverse'
              }
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  sortDir === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                }
                onClick={() =>
                  applySort(sortBy, sortDir === 'asc' ? 'desc' : 'asc')
                }
              >
                {sortDir === 'asc' ? 'Ascending' : 'Descending'}
              </Button>
            </Tooltip>

            <Button
              variant="contained"
              size="small"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClear}
            >
              Clear
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Matter #</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Offense / citation</TableCell>
                      <TableCell>County</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell>Court date</TableCell>
                      <TableCell>Last activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {search
                              ? 'No cases match that search.'
                              : state === 'all'
                                ? 'No cases yet.'
                                : `No ${state} cases.`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cases.map((item) => {
                        const closed = isClosedStage(
                          item.ticket,
                          item.ticketStatus?.TicketStatus_Name,
                        );
                        return (
                          <TableRow
                            key={item.ticket.Ticket_guid}
                            hover
                            sx={{ cursor: 'pointer', opacity: closed ? 0.55 : 1 }}
                            onClick={() =>
                              navigate(
                                `/ticket?Ticket_guid=${item.ticket.Ticket_guid}`,
                              )
                            }
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}
                              >
                                {item.ticket.Ticket_MatterNumber ||
                                  item.ticket.Ticket_Number ||
                                  '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {item.user
                                ? `${item.user.User_FirstName} ${item.user.User_LastName}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {item.ticket.Ticket_Description ||
                                item.ticket.Ticket_Citation ||
                                '—'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {caseCounty(item) ?? (
                                <Typography variant="body2" color="text.disabled">
                                  Not recorded
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={deriveStageLabel(
                                  item.ticket,
                                  item.ticketStatus?.TicketStatus_Name,
                                  item.hasFutureCourtEvent,
                                )}
                                color={closed ? 'default' : 'info'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatDate(item.ticket.Ticket_CourtDate)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title={formatDate(item.lastActivityAt)}>
                                <span>{describeAge(item.lastActivityAt)}</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={
                      loadingMore ? <CircularProgress size={20} /> : undefined
                    }
                  >
                    {loadingMore
                      ? 'Loading...'
                      : `Load more (showing ${cases.length} of ${total.toLocaleString('en-US')})`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerCasesView;
